import type { BigNumber } from "@ethersproject/bignumber"
import { useCallback } from "react"
import type { Address } from "viem"
import { EXTERNAL_ROUTES } from "@/common/constants/routes"
import {
  type FixedLSTToken,
  getLSTStakerConstants,
  getNullishLSTStakerConstants,
  type LSTData,
  type OrgToken,
  type RebasingLSTToken,
} from "@/governance/constants/lststaker"
import { useOrganization } from "@/organization/providers/OrganizationProvider"
import {
  type DelegateeData,
  useLSTDelegatee,
} from "@/stake/hooks/useLSTDelegatee"
import { useLSTUserBalances } from "@/stake/hooks/useLSTUserBalances"
import { getChainIdParams } from "@/web3/helpers/chainId"
import { useAddress } from "@/web3/hooks/useAddress"

type BalanceData = {
  value: BigNumber
  loading: boolean
  formatted: string
}

type TokenType = OrgToken | FixedLSTToken | RebasingLSTToken

export type Token<T extends TokenType = TokenType> = T & {
  concatBalance: (balance: string) => string
}

export type Response = {
  orgToken: Token<OrgToken>
  lstToken: Token<FixedLSTToken>
  rebasingLSTToken: Token<RebasingLSTToken>
  chainId: string
  uiConfig: LSTData["uiConfig"]
  user: {
    balances: {
      orgToken: BalanceData
      lstToken: BalanceData
    }
    delegatee: DelegateeData
  }
  update: () => void
  getTXLink: (hash: string, chainId?: string) => string | undefined
  allowNonEligibleDelegates: boolean
}

type Args = {
  orgId?: string
  accountAddress?: Address
}

function withConcatBalance<T extends TokenType>(token: T) {
  return {
    ...token,
    concatBalance: (balance: string) => `${balance} ${token.symbol}`,
  }
}

export function useLSTStakerData<T extends boolean = true>({
  orgId,
  accountAddress,
  throwIfEmpty = true as T,
}: Args & { throwIfEmpty?: T }): T extends false ? Response | null : Response {
  const globalOrg = useOrganization()
  const globalAddress = useAddress()
  const organizationId = orgId ?? globalOrg.organization?.id
  const userAddress = accountAddress ?? globalAddress

  // Always call hooks to respect rules of hooks
  const {
    orgTokenBalance,
    lstTokenBalance,
    update: updateBalances,
  } = useLSTUserBalances({
    orgId: organizationId,
    accountAddress: userAddress as Address | undefined,
  })
  const delegatee = useLSTDelegatee({
    orgId: organizationId,
    accountAddress: userAddress as Address | undefined,
  })

  const update = useCallback(() => {
    updateBalances()
    delegatee.update()
  }, [updateBalances, delegatee])

  // Check if LST data exists using nullish version
  const lstStakerConstants = !throwIfEmpty
    ? getNullishLSTStakerConstants(organizationId)
    : undefined

  // Use the throwing version if throwIfEmpty is true, otherwise use the nullish version
  const lstConstants = throwIfEmpty
    ? getLSTStakerConstants(organizationId)
    : lstStakerConstants

  const getTXLink = useCallback(
    (hash?: string, chainId?: string) => {
      if (!hash || !lstConstants) return

      return EXTERNAL_ROUTES.etherscan.hash(
        getChainIdParams(chainId || lstConstants.orgToken.chainId).reference,
        hash,
      )
    },
    [lstConstants],
  )

  if (!throwIfEmpty && !lstStakerConstants) {
    return null as T extends false ? Response | null : Response
  }

  // This will never be null if we reach this point and throwIfEmpty is true
  const {
    orgToken,
    lstToken,
    rebasingLSTToken,
    chainId,
    uiConfig,
    allowNonEligibleDelegates,
  } = lstConstants as NonNullable<typeof lstConstants>

  const result = {
    orgToken: withConcatBalance(orgToken),
    lstToken: withConcatBalance(lstToken),
    rebasingLSTToken: withConcatBalance(rebasingLSTToken),
    chainId,
    uiConfig,
    user: {
      balances: {
        orgToken: orgTokenBalance,
        lstToken: lstTokenBalance,
      },
      delegatee: {
        ...delegatee,
        // By default, the delegatee is LST Default delegatee even if the user has not delegated/staked
        // Return undefined if the user has no lst balance
        account: lstTokenBalance.value?.gt(0) ? delegatee.account : undefined,
      },
    },
    update,
    getTXLink,
    allowNonEligibleDelegates,
  }

  return result as T extends false ? Response | null : Response
}
